import React, {useCallback, useEffect, useState} from 'react';
import {album} from "../../../models/album";
import {BoxTitle} from "../BoxTitle";
import {ApiQueryParam, ApiService} from "../../../service/ApiService";
import {photoResultCollectionType, photoResultType} from "../../../types/Api/PhotoResultType";
import {photoHydrator} from "../../../models/hydrator/photoHydrator";
import {photo} from "../../../models/photo";
import {AlbumBoxPhotosLoader} from "../loader/AlbumBoxPhotosLoader";
import {Lightbox} from "./Lightbox";
import {NavLink} from "react-router-dom";
import {scrollTo} from "../../../service/ToolsService";

export type AlbumBoxProps = {
  album: album,
  back: () => void,
}

export const AlbumBox = ({album, back}: AlbumBoxProps) => {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([] as photo[]);
  const [selected, setSelected] = useState(undefined as undefined | number);

  const findPhoto = useCallback(async () => {
    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addFilter({
      field: 'albumId',
      value: album.albumId
    });
    apiFilters.addSort({
      by: 'createdAt',
      direction: 'ASC'
    });
    apiFilters.setCount(0);

    const result: photoResultCollectionType = await ApiService.get('/photo', apiFilters) as photoResultCollectionType;
    const photos: photo[] = result._embedded.photos.map((photo: photoResultType) => photoHydrator.hydrate(photo));

    setPhotos(photos)
  }, [album.albumId])

  useEffect(() => {
    setLoading(true);
    setPhotos([]);
    setSelected(undefined);
    findPhoto().finally(() => setLoading(false));
  }, [album, findPhoto]);

  return (
    <>
      <div className="__front_box__ mb-3">
        <BoxTitle title={album.label}/>

        <div className="d-flex align-items-center px-2 mb-3">
          <NavLink to="/photo" className="btn btn-sm btn-outline-primary" onClick={() => { scrollTo(); back(); }}>
            <i className="fa-solid fa-arrow-left me-2"/>Tous les albums
          </NavLink>
          {!loading && photos.length > 0 && (
            <span className="ms-auto text-muted small">
              <i className="fa-solid fa-camera me-1"/>{photos.length} photo{photos.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loading && (
          <div className="__album_masonry__">
            {[...new Array(8)].map((_, index: number) => (
              <div className="__album_masonry_item__" style={{height: index % 3 === 0 ? 220 : 160}} key={index}>
                <AlbumBoxPhotosLoader/>
              </div>
            ))}
          </div>
        )}

        {!loading && photos.length === 0 && (
          <p className="text-center text-muted py-4">Cet album ne contient pas encore de photo.</p>
        )}

        {!loading && photos.length > 0 && (
          <div className="__album_masonry__">
            {photos.map((photo: photo, index: number) => (
              <button key={photo.photoId} className="__album_masonry_item__ __album_photo__"
                      onClick={() => setSelected(index)} aria-label={`Agrandir la photo ${index + 1}`}>
                <img src={photo.src} alt={`${album.label} ${index + 1}`} loading="lazy" decoding="async"/>
                <span className="__album_photo_zoom__"><i className="fa-solid fa-magnifying-glass-plus"/></span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected !== undefined && (
        <Lightbox photos={photos} index={selected} title={album.label}
                  change={setSelected} close={() => setSelected(undefined)}/>
      )}
    </>
  );
}
